import { useMemo } from "react";
import { Info } from "lucide-react";

import { useLocalization } from "@i18n";

import type { InspectionConservationStateSummary } from "../../api/inspection";
import { buildInspectionEcColumns } from "../../catalog/inspection-ec-columns";

export interface InspectionConservationStateBandProps {
  conservationState?: InspectionConservationStateSummary;
  isLoading?: boolean;
}

/**
 * Faixa EC da tela inicial (inspection.md §6.1.1) — 5 cards selecionados no
 * visual do protótipo (ec-strip / ec-card brand). Somente leitura.
 */
const InspectionConservationStateBand = ({
  conservationState,
  isLoading = false,
}: InspectionConservationStateBandProps) => {
  const { translate } = useLocalization();
  const columns = useMemo(() => buildInspectionEcColumns(translate), [translate]);

  const selections = conservationState?.selections ?? [];
  const isComplete = Boolean(conservationState?.isComplete) && selections.length > 0;

  const cards = selections.flatMap((selection) => {
    const column = columns.find(
      (candidate) => candidate.responseKey === selection.columnKey,
    );
    if (!column) return [];

    const option = column.options.find(
      (candidate) => candidate.score === selection.selectedScore,
    );

    return [
      {
        key: column.key,
        title: column.title,
        letter: column.letter,
        score: selection.selectedScore,
        text: option?.text ?? "",
      },
    ];
  });

  return (
    <section
      aria-label={translate("inspection.conservationState.title")}
      className="insp-ec-strip"
    >
      <div className="insp-ec-caption">
        {translate("inspection.conservationState.caption")}
      </div>

      {isLoading ? (
        <div className="insp-ec-cards">
          {[0, 1, 2, 3, 4].map((index) => (
            <div
              key={index}
              className="h-[148px] animate-pulse rounded-lg bg-[var(--insp-surface-2)]"
            />
          ))}
        </div>
      ) : !isComplete || cards.length === 0 ? (
        <div className="insp-ec-empty">
          <Info aria-hidden size={16} />
          {translate("inspection.conservationState.empty")}
        </div>
      ) : (
        <div
          data-testid="inspection-ec-band-scroll"
          // overflow-x no JSDOM não é computado — inline mantém o container
          // detectável pelo drag-scroll compartilhado dos specs.
          style={{ overflowX: "auto" }}
          className="insp-ec-cards"
        >
          {cards.map((card) => (
            <div
              key={card.key}
              aria-label={`${card.key} ${card.score}`}
              className="insp-ec-card"
            >
              <div className="insp-ec-title">
                {`(${card.letter}) ${card.title}`}
              </div>
              <div className="insp-ec-desc">{card.text}</div>
              <div className="insp-ec-score">
                {String(card.score).padStart(2, "0")}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default InspectionConservationStateBand;
